import { ETabId } from '../../services/tab-configurations/tab-types';
import { TAppConfig } from '../api-ducks/app-config-duck/types-app-config';
import { TPaymentPartition, TPaymentPartitionGroup } from '../api-ducks/earnings-duck/types-earnings';
import { EPlanCadence, EUserRole, TCoreParticipation, TParticipation } from '../api-ducks/participation-details-duck/types-participation-details';
import { EPartitionCadence, TFilters, TPartition, TPartitionGroup } from './types-filters';

export const initialState: TFilters = {
  activeTab: ETabId.Global,
  selectedPaymentPartition: undefined,
  selectedPaymentPartitionGroup: undefined,
  availablePaymentPartitions: [],
  personnelNumber: 0,
};

export const getLatestConfiguredParticipation = (appConfig: TAppConfig, participation?: TParticipation): TCoreParticipation | undefined => {
  if (!participation) {
    return undefined;
  }

  // Ignore participations with FY greater than appConfigDefault FY
  let filteredParticipations = participation.coreParticipations.filter((p) => p.fiscalYearId <= Number(appConfig.defaultFiscalYearId));
  // If AppConfig default Fiscal Semester is present, ignore participations of greater semesters, unless
  // participations don't have a Fiscal Semester Id
  if (appConfig.defaultSemesterId) {
    filteredParticipations = filteredParticipations.filter((p) => !p.fiscalSemesterId || p.fiscalSemesterId <= Number(appConfig.defaultSemesterId));
  }
  if (filteredParticipations.length < 1) {
    return undefined;
  }

  filteredParticipations = filteredParticipations.filter((p) => p.userRole === EUserRole.NonParticipantManager || p.isPlanActive);

  // If no matching plans are found after filtering, return undefined
  if (filteredParticipations.length < 1) {
    return undefined;
  }

  // Filter any participations that start in the future
  const today = new Date();
  filteredParticipations = filteredParticipations.filter((p) => {
    if (p.userRole === EUserRole.NonParticipantManager) {
      return true;
    }
    const startDate = new Date(p.participationStartDateTime);
    return today >= startDate;
  });

  return filteredParticipations.reduce((latest, current) => {
    // Check if the fiscal year of this core participation is greater or equal than the current latest
    if (current.fiscalYearId >= latest.fiscalYearId) {
      // verify if semesterIds are present, if so pick largest
      if (current.fiscalSemesterId && latest.fiscalSemesterId) {
        // if both semesterId are the same, its a transfer scenario, pick largest EndDate
        if (latest.fiscalSemesterId === current.fiscalSemesterId) {
          return latest.participationEndDateTime > current.participationEndDateTime ? latest : current;
        }
        return latest.fiscalSemesterId > current.fiscalSemesterId ? latest : current;
      }
      // If both FY are the same, its a transfer scenario, pick largest EndDate
      if (latest.fiscalYearId === current.fiscalYearId) {
        return latest.participationEndDateTime > current.participationEndDateTime ? latest : current;
      }
      // If there are no SemesterId, and FY are not same, it means current is latest
      return current;
    }
    // If latestParticipation has a greater FY, then keep it as the latest.
    return latest;
  });
};

// Returns the default participation to show in the filters panel given a selected Fiscal Year.
// Current behavior: Pick latest that does not start in the future.
export const getDefaultParticipation = (participations: TCoreParticipation[]): TCoreParticipation => {
  const today = new Date();
  // Filter participations based on start date and plan status
  let currentParticipations = participations.filter((participation) => {
    const startDate = new Date(participation.participationStartDateTime);
    return today >= startDate;
  });

  currentParticipations = currentParticipations.filter((p) => p.isPlanActive);

  // We sort them by start date, and pick the latest one.
  const sortedParticipations = currentParticipations.sort(
    (a, b) => new Date(a.participationStartDateTime).getTime() - new Date(b.participationStartDateTime).getTime()
  );
  return sortedParticipations[sortedParticipations.length - 1];
};

// We give priority to Quarters, then Semesters, then Months.
export const getDefaultPartitionGroup = (
  participation?: TCoreParticipation,
  availablePartitions?: TPartitionGroup[],
  fiscalYear?: number
): TPartitionGroup => {
  // If there are no available partitions, we return an empty partition group.
  // An empty array must be treated the same as a missing one: a participation with no partitions
  // (for example an NPM placeholder for a fiscal year the user has no plan in) would otherwise
  // produce a group with no cadence, which suppresses the manager-to-participation filter sync.
  if (!participation || !availablePartitions || availablePartitions.length === 0) {
    return {
      partitionGroup: `${fiscalYear}`,
      partitionCadence: EPartitionCadence.Quarterly,
      partitions: [],
    };
  }

  // We sort manager partitions. (only Manager Partitions have partitionGroup)
  const sortedPartitions = [...availablePartitions];
  // If there is no fiscal year passed in, it means we have a mix of partitions from different fiscal years, so we pick the latest.
  if (fiscalYear === undefined) {
    sortedPartitions.sort((a, b) => b.partitionGroup?.localeCompare(a.partitionGroup || '') || 0);
  }
  let partitionGroup;

  // For semi annual plans, we give priority to SemiAnnual, then Quarter, then Monthly.
  if (participation.planCadence === EPlanCadence.SemiAnnual) {
    partitionGroup =
      sortedPartitions?.find((partition) => partition.partitionCadence === EPartitionCadence.SemiAnnual) ||
      availablePartitions?.find((partition) => partition.partitionCadence === EPartitionCadence.Quarterly) ||
      availablePartitions?.find((partition) => partition.partitionCadence === EPartitionCadence.Monthly) ||
      availablePartitions[0];
    // For Annual plans we give priority to Quarterly, then SemiAnnual, then Monthly.
  } else {
    partitionGroup =
      sortedPartitions?.find((partition) => partition.partitionCadence === EPartitionCadence.Quarterly) ||
      availablePartitions?.find((partition) => partition.partitionCadence === EPartitionCadence.SemiAnnual) ||
      availablePartitions?.find((partition) => partition.partitionCadence === EPartitionCadence.Monthly) ||
      availablePartitions[0];
  }
  const defaultPartitionGroup = { ...partitionGroup };
  if (!defaultPartitionGroup.partitionGroup) {
    defaultPartitionGroup.partitionGroup = `${fiscalYear}`;
  }
  return defaultPartitionGroup;
};

export const getParticipationById = (
  participationDetails: TParticipation,
  participationId: number,
  fiscalYearId: string
): TCoreParticipation | undefined =>
  participationDetails.coreParticipations.find(
    (participation) => participation.participationId === participationId && participation.fiscalYearId === Number(fiscalYearId)
  );

// This also doubles up to be used for Performance
export const getParticipationPartitionGroup = (
  cadence: EPartitionCadence,
  participationId: number,
  participationDetails: TParticipation,
  participationGroup: string
): TPartitionGroup | undefined => {
  const participation = getParticipationById(participationDetails, participationId, participationGroup);
  if (!participation) {
    return undefined;
  }
  return participation.availablePartitions.find((partition) => partition.partitionCadence === cadence);
};

export const getManagerPartitionGroup = (
  partitionGroupName: string | undefined,
  participationCadence?: EPartitionCadence,
  participationDetails?: TParticipation
): TPartitionGroup | undefined => {
  if (!partitionGroupName || !participationDetails || !participationDetails.availableManagerPartitions) return undefined;
  // Define the correct partition cadence. If not found default to Quarters, Then semesters, then months.
  let managerPartitionGroup = participationDetails.availableManagerPartitions?.find(
    (partition) => partition.partitionGroup === partitionGroupName && partition.partitionCadence === participationCadence
  );
  if (!managerPartitionGroup) {
    managerPartitionGroup = participationDetails.availableManagerPartitions?.find(
      (partition) => partition.partitionGroup === partitionGroupName && partition.partitionCadence === EPartitionCadence.Quarterly
    );
  }
  if (!managerPartitionGroup) {
    managerPartitionGroup = participationDetails.availableManagerPartitions?.find(
      (partition) => partition.partitionGroup === partitionGroupName && partition.partitionCadence === EPartitionCadence.SemiAnnual
    );
  }
  if (!managerPartitionGroup) {
    managerPartitionGroup = participationDetails.availableManagerPartitions?.find(
      (partition) => partition.partitionGroup === partitionGroupName && partition.partitionCadence === EPartitionCadence.Monthly
    );
  }
  return managerPartitionGroup;
};

export const getPaymentsPartitionGroup = (
  paritionGroupName: string,
  availablePaymentPartitions: TPaymentPartitionGroup[]
): TPaymentPartitionGroup | undefined => availablePaymentPartitions.find((partition) => partition.partitionGroup === paritionGroupName);

export const getPartition = (partitionTitle: string, partitionGroup?: TPartitionGroup): TPartition | undefined => {
  if (!partitionGroup) {
    return undefined;
  }
  if (!partitionGroup.partitions) {
    return undefined;
  }
  return partitionGroup.partitions.find((partition) => partition.title === partitionTitle);
};

export const getDefaultPartition = (partitions: TPartition[] | undefined): TPartition | undefined => {
  // Supporting scenarios where there are no partition groups. Ex: Non Participant Manager
  if (!partitions) return undefined;
  // We check if todays date falls into any given partition and return it.
  const today = new Date();
  const sortedPartitions = [...partitions].sort((a, b) => a.order - b.order);
  const defaultPartition = sortedPartitions.find((partition) => {
    const startDate = new Date(partition.startDate);
    const endDate = new Date(partition.endDate);
    return today >= startDate && today <= endDate;
  });
  if (defaultPartition) {
    return defaultPartition;
  }
  if (sortedPartitions.length < 1) {
    return undefined;
  }
  // If today's date doesn't fall into any partition, we return the last partition.
  return sortedPartitions[partitions.length - 1];
};

export const getPaymentPartition = (partitionId: string, partitionGroup?: TPaymentPartitionGroup): TPaymentPartition | undefined => {
  if (!partitionGroup) {
    return undefined;
  }
  return partitionGroup.partitions.find((partition) => partition.id === partitionId);
};

export const getPaymentPartitionByParticipationPartition = (
  paymentPartitions: TPaymentPartition[],
  participationPartition?: TPartition
): TPaymentPartition | undefined => {
  // We try to find the partition by codePeriod.
  let paymentPartition = paymentPartitions.find((partition) => partition.codePeriod === participationPartition?.title);

  // If not found via code period, we try to find by dates.
  // We pick the closest one to the selected participation partition.
  if (!paymentPartition) {
    paymentPartition = paymentPartitions.reduce((closest, current) => {
      const closestEndDate = new Date(closest.earningsEndDate);
      const currentEndDate = new Date(current.earningsEndDate);
      const participationEndDate = new Date(participationPartition?.endDate || '');
      if (Math.abs(participationEndDate.getTime() - closestEndDate.getTime()) > Math.abs(participationEndDate.getTime() - currentEndDate.getTime())) {
        return current;
      }
      return closest;
    }, paymentPartitions[0]);
  }
  return paymentPartition;
};

export const getDefaultPaymentPartition = (partitions: TPaymentPartition[]): TPaymentPartition | undefined => {
  // We check if todays date falls into any given partition and return it.
  const today = new Date();
  const sortedPartitions = [...partitions].sort((a, b) => {
    if (a.fiscalPartitionId === b.fiscalPartitionId) {
      return b.isTrueUp ? -1 : 1;
    }
    return a.fiscalPartitionId - b.fiscalPartitionId;
  });
  const defaultPartition = sortedPartitions.find((partition) => {
    const startDate = new Date(partition.earningsStartDate);
    const endDate = new Date(partition.earningsEndDate);
    return today >= startDate && today <= endDate;
  });
  if (defaultPartition) {
    return defaultPartition;
  }
  if (sortedPartitions.length < 1) {
    return undefined;
  }
  // If today's date doesn't fall into any partition, we return the last partition.
  return sortedPartitions[partitions.length - 1];
};

export const defaultFilters = (
  appConfig: TAppConfig,
  participation: TParticipation,
  availablePaymentPartitions?: TPaymentPartitionGroup[]
): TFilters => {
  const defaultParticipation = getLatestConfiguredParticipation(appConfig, participation);
  if (!defaultParticipation) {
    return initialState;
  }
  const { participationId, fiscalYearId } = defaultParticipation;
  const selectedParticipationPartitionGroup = getDefaultPartitionGroup(
    defaultParticipation,
    defaultParticipation.availablePartitions || [],
    fiscalYearId
  );
  const selectedParticipationPartition = getDefaultPartition(selectedParticipationPartitionGroup?.partitions);
  const selectedPerformancePartitionGroup = selectedParticipationPartitionGroup;
  const selectedPerformancePartition = selectedParticipationPartition;

  // Filter out years after the configured default fiscal year to select the default partition group for managers
  const availableManagerPartitions = participation.availableManagerPartitions?.filter(
    (partition) => Number(partition.partitionGroup) <= Number(appConfig.defaultFiscalYearId)
  );
  const selectedManagerPartitionGroup = getDefaultPartitionGroup(defaultParticipation, availableManagerPartitions || []);
  const selectedManagerPartition = getDefaultPartition(selectedManagerPartitionGroup?.partitions || []);
  const { personnelNumber } = participation;

  let selectedPaymentPartitionGroup;
  let selectedPaymentPartition;
  // If we already have available payment partitions, get and set the defaults.
  if (availablePaymentPartitions) {
    // Get the payment partition group for the selected (default) particiaption aprtition group name (fiscal year)
    selectedPaymentPartitionGroup = availablePaymentPartitions.find(
      (partition) => partition.partitionGroup === selectedParticipationPartitionGroup.partitionGroup
    );
    if (selectedPaymentPartitionGroup) {
      selectedPaymentPartition = selectedPaymentPartitionGroup.partitions.find(
        (partition) => partition.codePeriod === selectedParticipationPartition?.title
      );
      if (!selectedPaymentPartition) {
        selectedPaymentPartition = getDefaultPaymentPartition(selectedPaymentPartitionGroup.partitions);
      }
    }
  }
  return {
    participationId,
    personnelNumber,
    selectedParticipationPartitionGroup,
    selectedParticipationPartition,
    selectedManagerPartition,
    selectedManagerPartitionGroup,
    selectedPaymentPartitionGroup,
    selectedPaymentPartition,
    selectedPerformancePartition,
    selectedPerformancePartitionGroup,
  };
};
